import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ElNotification } from 'element-plus';
import { getUnreadCount, fetchInbox } from '@/api/inbox';
import { stripMessageMarkup } from '@/utils/messageMarkup';
import { useAuthStore } from '@/stores/auth.store';
import { useSessionStore } from '@/stores/session.store';
import { AUTH_TOKEN_KEY } from '@/api/client';

/**
 * Compteur global de messages non lus -- alimente les badges sur les
 * icones Inbox (HeaderGlobalNav desktop + MobileBottomNav).
 *
 * Poll toutes les 60s tant que l'AppLayout est monte. Volontairement plus
 * lent que la resource bar (15s) -- les messages sont moins critiques
 * que les ressources, et le total est rarement attendu en temps reel.
 * Si on branche SignalR pour les inbox events plus tard, on pourra
 * declencher refresh() en push et descendre l'interval a 5 min.
 */
const POLL_INTERVAL_MS = 60_000;

export const useInboxStore = defineStore('inbox', () => {
  const unreadCount = ref(0);
  const loading = ref(false);
  let inFlight: Promise<void> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  // Notifications de fin (construction / recherche / production…) : on toaste
  // les messages Système non lus jamais vus (id > lastMaxSystemId). prevCount
  // = -1 tant qu'on n'a pas fait de 1re passe (on n'alerte pas sur l'existant).
  let prevCount = -1;
  let lastMaxSystemId = -1;

  async function notifyNewSystemMessages(puId: number): Promise<void> {
    try {
      const res = await fetchInbox({ ownerPlayerUniverseId: puId, unreadOnly: true, messageType: 'System' });
      const ids = res.items.map(m => m.id);
      if (lastMaxSystemId < 0) { // 1re passe : init silencieuse
        if (ids.length) lastMaxSystemId = Math.max(...ids);
        return;
      }
      const fresh = res.items.filter(m => m.id > lastMaxSystemId).sort((a, b) => b.id - a.id).slice(0, 3);
      for (const m of fresh) {
        // Toast en texte brut (les icônes markup ne sont rendues que dans
        // l'inbox via <MessageBody>) -> on retire les jetons pour le titre/corps.
        ElNotification({ title: m.subject, message: stripMessageMarkup(m.body), type: 'success', duration: 6000 });
      }
      if (ids.length) lastMaxSystemId = Math.max(lastMaxSystemId, ...ids);
    } catch { /* silencieux */ }
  }

  const auth = useAuthStore();
  const session = useSessionStore();

  async function refresh(): Promise<void> {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      unreadCount.value = 0;
      return;
    }
    if (!auth.user || session.activePlayerUniverseId == null) return;
    if (inFlight) return inFlight;
    loading.value = true;
    inFlight = (async () => {
      try {
        // playerUniverseId (la partie active), PAS l'id du compte joueur.
        const puId = session.activePlayerUniverseId ?? 0;
        const newCount = await getUnreadCount(puId);
        // 1re passe (init lastMaxSystemId) OU le compteur monte -> on regarde
        // les nouveaux messages Système et on les toaste.
        if (prevCount < 0 || newCount > prevCount) {
          await notifyNewSystemMessages(puId);
        }
        prevCount = newCount;
        unreadCount.value = newCount;
      } catch {
        // Silencieux : un fail de count ne doit pas spammer l'utilisateur.
        // Le compteur garde sa derniere valeur connue.
      } finally {
        loading.value = false;
        inFlight = null;
      }
    })();
    return inFlight;
  }

  function start(): void {
    if (pollTimer) return;
    void refresh();
    pollTimer = setInterval(refresh, POLL_INTERVAL_MS);
  }

  function stop(): void {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function clear(): void {
    unreadCount.value = 0;
    prevCount = -1;
    lastMaxSystemId = -1;
  }

  return { unreadCount, loading, refresh, start, stop, clear };
});
